import { localValues, } from "@/storage/browser"
// @refresh reload

import { render } from "solid-js/web"
import { AppBaseProviders, AppInterface } from "@/app"
import { type Platform, PlatformProvider } from "@/context/platform"
import { dict as en } from "@/i18n/en"
import { captureNotificationHealth } from "@/context/notification-health"
import { notifyBrowser, resolveBrowserNotification } from "@/context/browser-notifications"
import { reloadWithCacheBust } from "@/utils/reload"
import { applyIOSBrowserViewportConstraints } from "@/utils/ios-safari-viewport"
import { installLayoutViewport } from "@/context/layout/viewport"
import { installFileDropGuard } from "@/utils/file-drop-guard"
import { installTouchInput } from "@/utils/touch-input"
import { RELEASE_VERSION } from "@/release"
import { ServerConnection } from "./context/server"
import { createSignal } from "solid-js"
import { appRouterBase } from "./utils/app-mode"
import { configuredControlApiCredentials, controlApiUrl } from "./transport/control-api"
import { exchangeAuthHandoff } from "./transport/auth"
import { type AppInfo, getAppInfo } from "./transport/app-info"
import { AppInfoProvider } from "./context/app-info"
import { consumeUserSessionHandoff, parseUserSessionHandoff } from "./storage/user-session"
import { consumeSidebarProjectLink } from "./storage/sidebar-project-filter"
import { consumeOnMachineLink } from "./storage/machine-locality"
import { consumeTabPanelsLink } from "./context/layout/tab-panels-link"
import { ConnectionError } from "./transport/connection-error"

const MUX_DEFAULT_SERVER_URL_KEY = "mux.settings.defaultServerUrl"

if (typeof document === "object" && typeof navigator === "object") {
  applyIOSBrowserViewportConstraints(document, navigator)
  // Before the first render: `md:` styling reads the mobile-layout attribute,
  // and hover styling reads the touch-input attribute.
  installLayoutViewport()
  installTouchInput(document)
  installFileDropGuard(document)
}

const getRootNotFoundError = () => en["error.dev.rootNotFound"]

const getStorage = (key: string) => {
  if (typeof localValues === "undefined") return null
  try {
    return localValues.getItem(key)
  } catch {
    return null
  }
}

const setStorage = (key: string, value: string | null) => {
  if (typeof localValues === "undefined") return
  try {
    if (value !== null) {
      localValues.setItem(key, value)
      return
    }
    localValues.removeItem(key)
  } catch {
    return
  }
}

const sanitizeUrl = (url: string | null | undefined) => {
  if (!url) return null
  try {
    const parsed = new URL(url.trim())
    parsed.username = ""
    parsed.password = ""
    let result = parsed.toString().replace(/\/+$/, "")
    return result
  } catch {
    return url.trim().replace(/\/+$/, "")
  }
}

const readDefaultServerUrl = () => sanitizeUrl(getStorage(MUX_DEFAULT_SERVER_URL_KEY))
const writeDefaultServerUrl = (url: string | null) => setStorage(MUX_DEFAULT_SERVER_URL_KEY, sanitizeUrl(url))

const sameOrigin = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a || !b) return false
  try {
    return new URL(a).origin === new URL(b).origin
  } catch {
    return a === b
  }
}

const notify: Platform["notify"] = async (...args) => {
  const health = captureNotificationHealth()
  try {
    await notifyBrowser(...args)
  } catch (error) {
    health.report(error)
  }
}

const resolveNotification: NonNullable<Platform["resolveNotification"]> = async (input) => {
  const health = captureNotificationHealth()
  try {
    await resolveBrowserNotification(input)
  } catch (error) {
    health.report(error)
  }
}

const openLink: Platform["openLink"] = (url) => {
  window.open(url, "_blank")
}

const back: Platform["back"] = () => {
  window.history.back()
}

const forward: Platform["forward"] = () => {
  window.history.forward()
}

const restart: Platform["restart"] = async () => {
  reloadWithCacheBust()
}

const root = document.getElementById("root")
if (!(root instanceof HTMLElement) && import.meta.env.DEV) {
  throw new Error(getRootNotFoundError())
}

const getCurrentUrl = (controlPlane: boolean) => {
  const configured = (import.meta.env.VITE_MUX_SERVER_URL)?.trim()
  if (configured) return sanitizeUrl(configured)

  if (controlPlane) return sanitizeUrl(controlApiUrl()) || ""

  // Use relative origin for machine access so SSE and API calls stay on the same connection.
  if (import.meta.env.PROD) {
    return sanitizeUrl(location.origin) || ""
  }

  if (import.meta.env.DEV) {
    const host = import.meta.env.VITE_MUX_SERVER_HOST ?? "localhost"
    const port = import.meta.env.VITE_MUX_SERVER_PORT ?? "4096"
    const protocol = host.includes("ts.net") ? "https" : "http"
    const portString = port === "443" ? "" : `:${port}`
    return `${protocol}://${host}${portString}`
  }
  return sanitizeUrl(location.origin) || ""
}

const disableHealthCheck = () => (import.meta.env.VITE_MUX_DISABLE_HEALTHCHECK) === "true"

const getDefaultUrl = (controlPlane: boolean) => {
  const lsDefault = readDefaultServerUrl()
  if (controlPlane && lsDefault && !sameOrigin(lsDefault, getCurrentUrl(controlPlane))) return getCurrentUrl(controlPlane)
  if (lsDefault) return lsDefault
  return getCurrentUrl(controlPlane)
}

const createPlatform = (controlPlane: boolean): Platform => ({
  platform: "web",
  version: RELEASE_VERSION,
  openLink,
  back,
  forward,
  restart,
  notify,
  resolveNotification,
  getDefaultServer: async () => {
    const stored = readDefaultServerUrl()
    if (controlPlane && stored && !sameOrigin(stored, getCurrentUrl(controlPlane))) return ServerConnection.Key.make(getCurrentUrl(controlPlane))
    return stored ? ServerConnection.Key.make(stored) : null
  },
  setDefaultServer: writeDefaultServerUrl,
})

const normalizeLocalStorage = (controlPlane: boolean) => {
  if (typeof localValues === "undefined") return
  try {
    if (controlPlane) {
      localValues.setItem(MUX_DEFAULT_SERVER_URL_KEY, getCurrentUrl(controlPlane))
      return
    }

    const defaultUrl = localValues.getItem(MUX_DEFAULT_SERVER_URL_KEY)
    if (!defaultUrl) return
    const sanitized = sanitizeUrl(defaultUrl)
    if (sanitized && sanitized !== defaultUrl) {
      localValues.setItem(MUX_DEFAULT_SERVER_URL_KEY, sanitized)
    }
  } catch (e) {
    console.error("Failed to normalize localValues", e)
  }
}

import { bootstrapLocalBrowser } from "@/transport/local-mode"

// index.html shows a boot screen until the app renders; its script detaches on this event.
const dismissBootScreen = () => {
  document.dispatchEvent(new Event("mux-boot-dismiss"))
  document.getElementById("mux-boot")?.remove()
}

const RootApp = (props: { appInfo: AppInfo }) => {
  const controlPlane = props.appInfo.role === "control-plane"
  const handoff = parseUserSessionHandoff(location.hash)
  const [handoffReady, setHandoffReady] = createSignal(!handoff.code)
  if (handoff.code) void consumeUserSessionHandoff(async (code, verifier) => {
    return exchangeAuthHandoff(code, verifier)
  }).finally(() => setHandoffReady(true))
  normalizeLocalStorage(controlPlane)
  const initialUrl = props.appInfo.localMode ? location.origin : getDefaultUrl(controlPlane)

  // Force splash only for stale localhost settings while we are remote.
  const shouldForceSplash = () => {
    const url = readDefaultServerUrl()
    return !!url && (url.includes("localhost") || url.includes("127.0.0.1")) && controlPlane
  }

  const localDevelopment = (import.meta.env.VITE_MUX_LOCAL_DEV) === "1"
  const isSignedIn = props.appInfo.localMode || localDevelopment || controlPlane || (!!getStorage("mux.auth.done") && !shouldForceSplash())
  const serverUrl = initialUrl
  const serverCredentials = configuredControlApiCredentials()
  const serverUsername = serverCredentials.username
  const serverPassword = serverCredentials.password

  const fleetMode = controlPlane
  const platform = createPlatform(controlPlane)

  return (
    <PlatformProvider value={platform}>
      {handoffReady() ? (
        <AppBaseProviders>
          <AppInterface
            defaultServer={ServerConnection.Key.make(serverUrl!)}
            servers={fleetMode ? [] : [{ type: "http", displayName: props.appInfo.localMode ? "local" : undefined, http: { url: serverUrl!, username: serverUsername, password: serverPassword } }]}
            startInactive={fleetMode || !isSignedIn}
            disableHealthCheck={disableHealthCheck()}
            base={appRouterBase(localDevelopment)}
          />
        </AppBaseProviders>
      ) : null}
    </PlatformProvider>
  )
}

if (root instanceof HTMLElement) {
  // Before the router reads the address: "Open in new tab" links scope this tab's sidebar and panels.
  consumeSidebarProjectLink()
  consumeTabPanelsLink()
  consumeOnMachineLink()
  void bootstrapLocalBrowser().then(() => getAppInfo())
    .then((appInfo) => {
      dismissBootScreen()
      render(() => (
        <AppInfoProvider value={appInfo}>
          <RootApp appInfo={appInfo} />
        </AppInfoProvider>
      ), root)
    })
    .catch((error) => {
      dismissBootScreen()
      // Without a connection, reload once it returns rather than wait for a tap.
      const unreachable = error instanceof ConnectionError || navigator.onLine === false
      if (unreachable) window.addEventListener("online", () => location.reload(), { once: true })
      render(() => (
        <main class="grid min-h-dvh place-items-center bg-[#07080a] p-6 text-[#f3f4f6]">
          <div class="max-w-md text-center">
            <h1 class="text-lg font-semibold">{unreachable ? "Kettle can’t reach the server" : "Could not start Kettle"}</h1>
            <p class="mt-2 text-sm text-white/55">
              {unreachable
                ? "Check your connection. Kettle reloads when it is back."
                : error instanceof Error ? error.message : "Could not identify this Kettle server"}
            </p>
            <button class="mt-5 rounded-md border border-white/15 px-4 py-2 text-sm hover:bg-white/5" onClick={() => location.reload()}>
              Retry
            </button>
          </div>
        </main>
      ), root)
    })
}
